import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { useCallback, useMemo } from 'react'
import { Env } from '@/configs'
import { PromisedResult, toAsyncResult } from '@/utils/result'
import { useAuthDependencies } from './auth.dependencies'
import { useAlert } from '../alert/alert.context'
import { useRouting } from '@/hooks'
import { ApiErrorHandler } from '../api/errorHandler.service'

type AuthAPICaller = <T>(config: AxiosRequestConfig) => PromisedResult<T>

export function useAuthAPI(): AuthAPICaller {
  const { sendAlert } = useAlert()
  const { unAuthorize } = useAuthDependencies()
  const { navigate } = useRouting()

  const errorHandler = useMemo(() => {
    return new ApiErrorHandler({
      sendAlert,
      unAuthorize,
      navigate,
    })
  }, [sendAlert, unAuthorize, navigate])

  const instance = useMemo(() => {
    const baseURL = process.env[Env.REACT_APP_API_URL] ?? 'https://localhost:8080'
    const axiosInstance = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    })
    axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          const errorData = error.response.data as any
          if (errorData?.message === 'missing token error') {
            return Promise.reject(error)
          }
        }
        return errorHandler.handleError(error)
      },
    )

    return axiosInstance
  }, [errorHandler])

  const caller = useCallback(
    async <T>(config: AxiosRequestConfig): PromisedResult<T> => {
      const [error, response] = await toAsyncResult(instance(config))
      if (error) {
        return [error]
      }

      const { data } = response as AxiosResponse<T>
      return [undefined, data]
    },
    [instance],
  )

  return caller
}
